import React, { useEffect, useState, useCallback } from "react";
import { Alert, Spin } from "antd";
import opportunityValidationService, {
    getDefaultApnDetails,
    getDefaultFinancePartnerDetails,
    getDefaultJobPhotos,
    getDefaultNotes,
    getDefaultProductDetails,
    getDefaultUtilityDetails,
} from "../../../../../services/oneButtonServices/opportunityValidationService";
import { OpportunityValidationCollapse, OpportunityValidationCollapseProps } from "./OpportunityValidationCollapse";
import DashboardModal from "../DashboardModal";

interface OpportunityValidationModalProps {
    open: boolean;
    onCancel: () => void;
    opportunityId: string;
    /** Matches oneBUTTON / dashboard theme for modal chrome */
    theme?: 'dark' | 'light';
}

export const OpportunityValidationModal: React.FC<OpportunityValidationModalProps> = (props) => {
    const theme = props.theme ?? 'dark';
    const [collapseProps, setCollapseProps] = useState<OpportunityValidationCollapseProps | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const salesforceId = sessionStorage.getItem("SalesForceId");

    type LoadResult =
        | { ok: true; data: OpportunityValidationCollapseProps }
        | { ok: false; error: string };

    const loadValidationData = useCallback(async (oppId: string): Promise<LoadResult> => {
        const id = String(oppId ?? '').trim();
        if (!id) {
            return { ok: false, error: 'No opportunity ID is available.' };
        }

        const oppDetails = await opportunityValidationService.getOppDetails(id).catch(() => null);
        if (!oppDetails?.opportunityInfo || !oppDetails?.statusIndicators) {
            return {
                ok: false,
                error: 'Could not load opportunity validation data. Check the opportunity ID and try again.',
            };
        }

        const [
            fpDetails,
            apnDetails,
            productDetails,
            utilityDetails,
            jobPhotos,
            notes,
            systemDesign,
        ] = await Promise.all([
            opportunityValidationService.getFinancePartnerDetails(id).catch(() => getDefaultFinancePartnerDetails()),
            opportunityValidationService.getApnDetails(id).catch(() => getDefaultApnDetails()),
            opportunityValidationService.getProductDetails(id).catch(() => getDefaultProductDetails()),
            opportunityValidationService.getUtilityDetails(id).catch(() => getDefaultUtilityDetails()),
            opportunityValidationService.getJobPhotos(id).catch(() => getDefaultJobPhotos()),
            opportunityValidationService.getNotes(id).catch(() => getDefaultNotes()),
            opportunityValidationService.getSystemDesignDetails(id).catch(() => []),
        ]);

        return {
            ok: true,
            data: {
                opportunityId: id,
                statusIndicators: oppDetails.statusIndicators,
                opportunity: oppDetails.opportunityInfo,
                financePartner: fpDetails,
                apn: apnDetails,
                product: productDetails,
                utility: utilityDetails,
                photos: jobPhotos,
                notes: notes,
                systemDesign: Array.isArray(systemDesign) ? systemDesign : [],
            },
        };
    }, []);

    useEffect(() => {
        if (!props.open) {
            return;
        }

        const id = String(props.opportunityId ?? '').trim();
        if (!id) {
            setLoadError('No opportunity ID is available.');
            setCollapseProps(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setCollapseProps(null);

        loadValidationData(id)
            .then((result) => {
                if (cancelled) return;
                if (result.ok) {
                    setCollapseProps(result.data);
                    setLoadError(null);
                } else {
                    setCollapseProps(null);
                    setLoadError(result.error);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.open, props.opportunityId, loadValidationData]);

    return (
        <DashboardModal
            title={`Opportunity Validation${salesforceId ? ` - ${salesforceId}` : ''}`}
            width="80%"
            open={props.open}
            onClose={() => props.onCancel()}
            zIndex={9610}
            theme={theme}
            rootClassName="obd-opp-validation-modal-root"
        >
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 24px' }}>
                    <Spin size="large" tip="Loading validation data…" />
                </div>
            )}
            {!loading && loadError && (
                <Alert type="error" message={loadError} showIcon style={{ marginBottom: 16 }} />
            )}
            {!loading && collapseProps && (
                <OpportunityValidationCollapse key={collapseProps.opportunityId} {...collapseProps} />
            )}
        </DashboardModal>
    );
};
